import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, Worker } from '../lib/supabase';
import {
  ArrowLeft,
  Award,
  FileText,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Users,
  Calendar,
  Search,
  Upload,
  Trash2,
  AlertCircle,
  CreditCard,
  CheckCircle,
  Edit2,
  X,
  Save,
  Image as ImageIcon,
} from 'lucide-react';

interface TrainingRecord {
  id: string;
  worker_id: string;
  title: string;
  document_url: string | null;
  document_type: string | null;
  uploaded_at: string | null;
  created_at: string;
}

interface WorkerWithTraining extends Worker {
  training_records: TrainingRecord[];
  drivers_licence_number: string | null;
  drivers_licence_expiry: string | null;
  drivers_licence_photo_url: string | null;
}

interface LicenceFormState {
  number: string;
  expiry: string;
  file: File | null;
}

const DAYS_WARNING = 30;

function daysUntilExpiry(expiry: string | null): number | null {
  if (!expiry) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const exp = new Date(expiry);
  return Math.round((exp.getTime() - now.getTime()) / 86400000);
}

function LicenceStatusBadge({ expiry }: { expiry: string | null }) {
  const days = daysUntilExpiry(expiry);
  if (days === null) return null;
  if (days < 0) return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
      <AlertCircle className="h-3 w-3" /> Expired
    </span>
  );
  if (days <= DAYS_WARNING) return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
      <AlertCircle className="h-3 w-3" /> Expires in {days}d
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
      <CheckCircle className="h-3 w-3" /> Valid
    </span>
  );
}

export const WorkerTrainingPage: React.FC = () => {
  const navigate = useNavigate();
  const [workers, setWorkers] = useState<WorkerWithTraining[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedWorkers, setExpandedWorkers] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState<string | null>(null);
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newDocType, setNewDocType] = useState('');
  const [newFile, setNewFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Licence editing state — keyed by worker id
  const [editingLicence, setEditingLicence] = useState<string | null>(null);
  const [licenceForm, setLicenceForm] = useState<LicenceFormState>({ number: '', expiry: '', file: null });
  const [licenceSubmitting, setLicenceSubmitting] = useState(false);
  const [licenceError, setLicenceError] = useState<string | null>(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [workersRes, trainingRes] = await Promise.all([
        supabase.from('workers').select('*').order('name'),
        supabase.from('worker_training_records').select('*').order('uploaded_at', { ascending: false }),
      ]);
      if (workersRes.error) throw workersRes.error;
      if (trainingRes.error) throw trainingRes.error;

      const trainingByWorker = new Map<string, TrainingRecord[]>();
      (trainingRes.data || []).forEach(r => {
        if (!trainingByWorker.has(r.worker_id)) trainingByWorker.set(r.worker_id, []);
        trainingByWorker.get(r.worker_id)!.push(r);
      });

      const combined: WorkerWithTraining[] = (workersRes.data || []).map(w => ({
        ...w,
        training_records: trainingByWorker.get(w.id) || [],
        drivers_licence_number: w.drivers_licence_number ?? null,
        drivers_licence_expiry: w.drivers_licence_expiry ?? null,
        drivers_licence_photo_url: w.drivers_licence_photo_url ?? null,
      }));
      setWorkers(combined);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleWorker = (id: string) => {
    setExpandedWorkers(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openAddForm = (workerId: string) => {
    setAddingFor(workerId);
    setNewTitle('');
    setNewDocType('');
    setNewFile(null);
    setError(null);
    setExpandedWorkers(prev => new Set(prev).add(workerId));
  };

  const cancelAdd = () => {
    setAddingFor(null);
    setNewTitle('');
    setNewDocType('');
    setNewFile(null);
    setError(null);
  };

  const handleAdd = async (workerId: string) => {
    if (!newTitle.trim()) { setError('Title is required.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      let documentUrl: string | null = null;
      if (newFile) {
        setUploading(workerId);
        const ext = newFile.name.split('.').pop();
        const path = `training/${workerId}/${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('documents').upload(path, newFile, { upsert: false });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage.from('documents').getPublicUrl(path);
        documentUrl = urlData.publicUrl;
        setUploading(null);
      }
      const { error: insertError } = await supabase.from('worker_training_records').insert([{
        worker_id: workerId,
        title: newTitle.trim(),
        document_type: newDocType.trim() || null,
        document_url: documentUrl,
        uploaded_at: new Date().toISOString(),
      }]);
      if (insertError) throw insertError;
      cancelAdd();
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to save record.');
      setUploading(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (recordId: string) => {
    if (!confirm('Delete this training record?')) return;
    const { error } = await supabase.from('worker_training_records').delete().eq('id', recordId);
    if (!error) fetchData();
  };

  const openLicenceEdit = (worker: WorkerWithTraining) => {
    setEditingLicence(worker.id);
    setLicenceForm({
      number: worker.drivers_licence_number || '',
      expiry: worker.drivers_licence_expiry || '',
      file: null,
    });
    setLicenceError(null);
    setExpandedWorkers(prev => new Set(prev).add(worker.id));
  };

  const cancelLicenceEdit = () => {
    setEditingLicence(null);
    setLicenceForm({ number: '', expiry: '', file: null });
    setLicenceError(null);
  };

  const saveLicence = async (workerId: string) => {
    setLicenceSubmitting(true);
    setLicenceError(null);
    try {
      let photoUrl: string | null | undefined = undefined;

      if (licenceForm.file) {
        const ext = licenceForm.file.name.split('.').pop();
        const path = `licences/${workerId}/${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('documents').upload(path, licenceForm.file, { upsert: true });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage.from('documents').getPublicUrl(path);
        photoUrl = urlData.publicUrl;
      }

      const updates: Record<string, string | null> = {
        drivers_licence_number: licenceForm.number.trim() || null,
        drivers_licence_expiry: licenceForm.expiry || null,
      };
      if (photoUrl !== undefined) updates.drivers_licence_photo_url = photoUrl;

      const { error: updateError } = await supabase.from('workers')
        .update(updates).eq('id', workerId);
      if (updateError) throw updateError;

      cancelLicenceEdit();
      fetchData();
    } catch (err: any) {
      setLicenceError(err.message || 'Failed to save licence.');
    } finally {
      setLicenceSubmitting(false);
    }
  };

  const filteredWorkers = workers.filter(w =>
    w.name.toLowerCase().includes(search.toLowerCase()) ||
    w.role.toLowerCase().includes(search.toLowerCase())
  );
  const regularWorkers = filteredWorkers.filter(w => w.role?.toLowerCase() !== 'visitor');

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Count workers with expiring/expired licences for top-level alert
  const licenceAlerts = regularWorkers.filter(w => {
    const d = daysUntilExpiry(w.drivers_licence_expiry);
    return d !== null && d <= DAYS_WARNING;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/workers')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Training &amp; Certificates</h1>
            <p className="text-sm text-gray-500 mt-0.5">All worker certificates and training records</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <Users className="h-4 w-4" />
          <span>{regularWorkers.length} workers</span>
          <span className="text-gray-300 mx-1">·</span>
          <Award className="h-4 w-4" />
          <span>{regularWorkers.reduce((sum, w) => sum + w.training_records.length, 0)} records</span>
        </div>
      </div>

      {/* Licence expiry alert banner */}
      {licenceAlerts.length > 0 && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              {licenceAlerts.length} driver{licenceAlerts.length > 1 ? 's' : ''} with licence expiring soon or expired
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              {licenceAlerts.map(w => {
                const d = daysUntilExpiry(w.drivers_licence_expiry);
                return `${w.name} (${d !== null && d < 0 ? 'expired' : `${d}d`})`;
              }).join(' · ')}
            </p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search workers..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
        />
      </div>

      {/* Worker Cards */}
      <div className="space-y-3">
        {regularWorkers.length === 0 && (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No workers found.</p>
          </div>
        )}

        {regularWorkers.map(worker => {
          const isExpanded = expandedWorkers.has(worker.id);
          const isAdding = addingFor === worker.id;
          const isEditingLic = editingLicence === worker.id;
          const count = worker.training_records.length;
          const licDays = daysUntilExpiry(worker.drivers_licence_expiry);
          const hasLicAlert = licDays !== null && licDays <= DAYS_WARNING;

          return (
            <div key={worker.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              {/* Worker row */}
              <div
                className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors select-none"
                onClick={() => toggleWorker(worker.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                    <Users className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900">{worker.name}</p>
                      {hasLicAlert && (
                        <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Licence {licDays !== null && licDays < 0 ? 'expired' : `exp. ${licDays}d`}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 capitalize">{worker.role.replace('_', ' ')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                    count > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {count} {count === 1 ? 'record' : 'records'}
                  </span>
                  <button
                    onClick={e => { e.stopPropagation(); openAddForm(worker.id); }}
                    className="p-1.5 rounded-md bg-orange-50 hover:bg-orange-100 text-orange-600 transition-colors"
                    title="Add record"
                  >
                    <Upload className="h-3.5 w-3.5" />
                  </button>
                  {isExpanded
                    ? <ChevronUp className="h-4 w-4 text-gray-400" />
                    : <ChevronDown className="h-4 w-4 text-gray-400" />
                  }
                </div>
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <div className="border-t border-gray-100 bg-gray-50 px-5 py-4 space-y-4">

                  {/* ── Driver's Licence Section ── */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-semibold text-gray-700">Driver's Licence</span>
                      </div>
                      {!isEditingLic && (
                        <button
                          onClick={() => openLicenceEdit(worker)}
                          className="flex items-center gap-1.5 text-xs text-orange-600 hover:text-orange-700 font-medium px-2.5 py-1 rounded-md hover:bg-orange-50 transition-colors"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                          {worker.drivers_licence_number ? 'Edit' : 'Add'}
                        </button>
                      )}
                    </div>

                    {isEditingLic ? (
                      <div className="p-4 space-y-3">
                        {licenceError && (
                          <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                            <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {licenceError}
                          </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Licence Number</label>
                            <input
                              type="text"
                              placeholder="e.g. 12345678"
                              value={licenceForm.number}
                              onChange={e => setLicenceForm(f => ({ ...f, number: e.target.value }))}
                              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Expiry Date</label>
                            <input
                              type="date"
                              value={licenceForm.expiry}
                              onChange={e => setLicenceForm(f => ({ ...f, expiry: e.target.value }))}
                              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Licence Photo {worker.drivers_licence_photo_url ? '(upload new to replace)' : '(optional)'}
                          </label>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={e => setLicenceForm(f => ({ ...f, file: e.target.files?.[0] || null }))}
                            className="w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={cancelLicenceEdit}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                          >
                            <X className="h-3.5 w-3.5" /> Cancel
                          </button>
                          <button
                            onClick={() => saveLicence(worker.id)}
                            disabled={licenceSubmitting}
                            className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-60 transition-colors font-medium"
                          >
                            <Save className="h-3.5 w-3.5" />
                            {licenceSubmitting ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    ) : worker.drivers_licence_number || worker.drivers_licence_expiry || worker.drivers_licence_photo_url ? (
                      <div className="px-4 py-3 flex items-center gap-4 flex-wrap">
                        {worker.drivers_licence_photo_url && (
                          <a
                            href={worker.drivers_licence_photo_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative h-16 w-24 rounded-lg border border-gray-200 overflow-hidden bg-gray-100 shrink-0 hover:border-blue-400 transition-colors"
                            title="View licence photo"
                          >
                            <img
                              src={worker.drivers_licence_photo_url}
                              alt="Licence"
                              className="h-full w-full object-cover"
                              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                              <ExternalLink className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </a>
                        )}
                        {!worker.drivers_licence_photo_url && (
                          <div className="h-16 w-24 rounded-lg border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center shrink-0">
                            <ImageIcon className="h-5 w-5 text-gray-300" />
                          </div>
                        )}
                        <div className="space-y-1.5 min-w-0">
                          {worker.drivers_licence_number && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 w-16 shrink-0">Number</span>
                              <span className="text-sm font-mono font-medium text-gray-800">{worker.drivers_licence_number}</span>
                            </div>
                          )}
                          {worker.drivers_licence_expiry && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 w-16 shrink-0">Expires</span>
                              <span className={`text-sm font-medium ${hasLicAlert ? (licDays !== null && licDays < 0 ? 'text-red-600' : 'text-amber-600') : 'text-gray-800'}`}>
                                {formatDate(worker.drivers_licence_expiry)}
                              </span>
                              <LicenceStatusBadge expiry={worker.drivers_licence_expiry} />
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="px-4 py-4 text-center">
                        <p className="text-xs text-gray-400">No licence details recorded.</p>
                        <button
                          onClick={() => openLicenceEdit(worker)}
                          className="mt-1.5 text-xs text-orange-600 hover:text-orange-700 font-medium"
                        >
                          Add licence
                        </button>
                      </div>
                    )}
                  </div>

                  {/* ── Training Records Section ── */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Training Records</span>
                    </div>

                    {/* Add form */}
                    {isAdding && (
                      <div className="bg-white rounded-lg border border-orange-200 p-4 space-y-3 mb-3">
                        <p className="text-sm font-medium text-gray-800">Add Training Record</p>
                        {error && (
                          <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                            <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {error}
                          </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
                            <input
                              type="text"
                              placeholder="e.g. White Card, First Aid"
                              value={newTitle}
                              onChange={e => setNewTitle(e.target.value)}
                              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Document Type</label>
                            <input
                              type="text"
                              placeholder="e.g. Certificate, Licence"
                              value={newDocType}
                              onChange={e => setNewDocType(e.target.value)}
                              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Attach Document (optional)</label>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            onChange={e => setNewFile(e.target.files?.[0] || null)}
                            className="w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={cancelAdd}
                            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleAdd(worker.id)}
                            disabled={submitting || uploading === worker.id}
                            className="px-4 py-1.5 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-60 transition-colors font-medium"
                          >
                            {submitting ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Records list */}
                    {worker.training_records.length === 0 && !isAdding ? (
                      <div className="text-center py-6 bg-white rounded-lg border border-gray-200">
                        <Award className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-xs text-gray-400">No training records yet.</p>
                        <button
                          onClick={() => openAddForm(worker.id)}
                          className="mt-2 text-xs text-orange-600 hover:text-orange-700 font-medium"
                        >
                          Add first record
                        </button>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white overflow-hidden">
                        {worker.training_records.map(record => (
                          <div
                            key={record.id}
                            className={`flex items-center justify-between px-4 py-3 transition-colors group ${record.document_url ? 'cursor-pointer hover:bg-blue-50' : 'hover:bg-gray-50'}`}
                            onClick={() => record.document_url && window.open(record.document_url, '_blank', 'noopener,noreferrer')}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${record.document_url ? 'bg-orange-50 group-hover:bg-blue-100' : 'bg-orange-50'}`}>
                                <FileText className={`h-4 w-4 transition-colors ${record.document_url ? 'text-orange-600 group-hover:text-blue-600' : 'text-orange-600'}`} />
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className={`text-sm font-medium truncate transition-colors ${record.document_url ? 'text-gray-800 group-hover:text-blue-700' : 'text-gray-800'}`}>{record.title}</p>
                                  {record.document_url && <ExternalLink className="h-3 w-3 text-gray-300 group-hover:text-blue-500 shrink-0 transition-colors" />}
                                </div>
                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                  {record.document_type && (
                                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                                      {record.document_type}
                                    </span>
                                  )}
                                  <span className="flex items-center gap-1 text-xs text-gray-400">
                                    <Calendar className="h-3 w-3" />
                                    {formatDate(record.uploaded_at || record.created_at)}
                                  </span>
                                  {!record.document_url && (
                                    <span className="text-xs text-gray-300 italic">No document attached</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 ml-3 shrink-0">
                              {record.document_url && (
                                <span className="hidden group-hover:flex items-center gap-1 text-xs text-blue-600 font-medium">
                                  View <ExternalLink className="h-3 w-3" />
                                </span>
                              )}
                              <button
                                onClick={e => { e.stopPropagation(); handleDelete(record.id); }}
                                className="p-1.5 rounded-md hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                                title="Delete record"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
